import { useState, useEffect, useRef, useCallback } from 'react';
import { useIssueStore, useProjectStore } from '@/stores';
import client from '@/api/client';
import type { Issue, IssueListType, PlanTask, TaskDelayRecord, PlanTaskStatus, ActionItem, ActionItemStatus, ActionItemDelayRecord, Attachment } from '@/types';

interface EditFormPlanTask {
  id: string | undefined;
  content: string;
  plannedDate: string;
  status: PlanTaskStatus;
  delayReason: string;
  newPlannedDate: string;
  actualDate: string;
  delayHistory: TaskDelayRecord[];
  delayAction?: 'completed' | 'delay-again';
  newDelayReason?: string;
  newDelayPlannedDate?: string;
  delayAction2?: 'completed' | 'delay-again';
  actualDate2?: string;
}

interface EditFormActionItem {
  id?: string;
  content: string;
  breakpoint: string;
  node: string;
  status: ActionItemStatus;
  delayReason: string;
  newPlannedDate: string;
  actualDate: string;
  newBpNode: string;
  delayHistory: ActionItemDelayRecord[];
  expandBpNg?: 'completed' | 'bp-again';
  newBpReason?: string;
  newBpDate?: string;
  newBpNode2?: string;
  expandBpNg2?: 'completed' | 'bp-again';
  actualDate2?: string;
}

const ACTION_ITEM_STATUS_LABELS: Record<ActionItemStatus, string> = {
  'pending': '待执行',
  'completed': '已完成',
  'bp-ng': 'BP失效',
  'delayed-complete': '延期完成',
};

const ACTION_ITEM_STATUS_COLORS: Record<ActionItemStatus, string> = {
  'pending': 'border-gray-300',
  'completed': 'bg-green-500 border-green-500',
  'bp-ng': 'bg-red-500 border-red-500',
  'delayed-complete': 'bg-orange-500 border-orange-500',
};

const nativeConfirm = window.confirm;
import {
  RISK_STATUS_COLORS,
  ISSUE_STATUS_COLORS,
  ISSUE_TYPE_LABELS,
  ISSUE_STATUS_LABELS,
  PLAN_TASK_STATUS_COLORS,
  PLAN_TASK_STATUS_LABELS,
  ISSUE_TYPE_LABELS as ISSUE_TYPE_LABELS_MAP,
} from '@/types';
import {
  ChevronDown,
  ChevronUp,
  Plus,
  Upload,
  Download,
  Filter,
  Trash2,
  Edit2,
  MoreHorizontal,
  X,
  Hash,
  AlertTriangle,
  FileText,
  CheckCircle,
  User,
  Settings,
  Paperclip
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { Button, Card, CardContent, Dock } from '@/components/ui';
import { IssueCreateModal } from './IssueCreateModal';
import { IssueEditModal } from './IssueEditModal';
import { IssueImportModal } from './IssueImportModal';
import { ProgressBar } from './ProgressBar';

export function IssuesList() {
  const { 
    productIssues, 
    productionIssues, 
    activeTab, 
    setActiveTab,
    filters,
    expandedIssueId,
    setExpandedIssueId,
    setFilters,
    resetFilters
  } = useIssueStore();

  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [editingIssue, setEditingIssue] = useState<Issue | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  const issueRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());

  const { completeTask, delayTask, updatePlanTask, updatePlanTaskLocally } = useIssueStore();

  const issues = activeTab === 'product' ? productIssues : productionIssues;

  useEffect(() => {
    if (expandedIssueId) {
      setExpandedRows(prev => {
        if (!prev.has(expandedIssueId)) {
          const newExpanded = new Set(prev);
          newExpanded.add(expandedIssueId);
          return newExpanded;
        }
        return prev;
      });
      
      setTimeout(() => {
        const rowElement = issueRefs.current.get(expandedIssueId);
        if (rowElement) {
          rowElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          const element = document.querySelector(`[data-issue-id="${expandedIssueId}"]`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      }, 100);
      
      setExpandedIssueId(null);
    }
  }, [expandedIssueId, setExpandedIssueId]);

  const toggleRow = (id: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const handleEditIssue = (issueId: string) => {
    const latestIssue = issues.find(i => i.id === issueId);
    if (latestIssue) {
      setEditingIssue(latestIssue);
    }
    if (!expandedRows.has(issueId)) {
      const newExpanded = new Set(expandedRows);
      newExpanded.add(issueId);
      setExpandedRows(newExpanded);
    }
  };

  const handleTaskComplete = (taskId: string) => {
    const today = new Date().toISOString().split('T')[0];
    updatePlanTaskLocally(taskId, { status: 'completed', actualDate: today });
    completeTask(taskId, { actualDate: today }).catch((error) => {
      console.error('Failed to complete task:', error);
      updatePlanTaskLocally(taskId, { status: 'pending', actualDate: undefined });
    });
  };

  const filteredIssues = issues.filter(issue => {
    if (filters.status.length > 0 && !filters.status.includes(issue.status)) return false;
    if (filters.type.length > 0 && !filters.type.includes(issue.type)) return false;
    if (filters.riskStatus.length > 0 && !filters.riskStatus.includes(issue.riskStatus)) return false;
    if (filters.keyword) {
      const keyword = filters.keyword.toLowerCase();
      if (!issue.description.toLowerCase().includes(keyword) && 
          !issue.owner?.toLowerCase().includes(keyword)) {
        return false;
      }
    }
    return true;
  });

  const exportIssuesToExcel = () => {
    const currentIssues = filteredIssues;
    if (currentIssues.length === 0) {
      alert('没有可导出的问题');
      return;
    }

    const isProduct = activeTab === 'product';

    const data = currentIssues.map(issue => {
      const shortTermActions = issue.actionItems?.filter(a => a.actionType === 'short_term') || [];
      const longTermActions = issue.actionItems?.filter(a => a.actionType === 'long_term') || [];
      
      const baseData: Record<string, string> = {
        '序号': String(issue.sequence).padStart(3, '0'),
        '描述': issue.description,
        '状态': issue.status,
        '工作计划': (issue.planTasks && issue.planTasks.length > 0)
          ? issue.planTasks.map(t => `${t.content} (${t.plannedDate || '-'} - ${PLAN_TASK_STATUS_LABELS[t.status]})`).join('; ')
          : '-',
        '负责人': issue.owner || '-',
        '原因分析': issue.rootCause || '-',
        '短期措施': shortTermActions.map(a => a.content).join('; ') || issue.shortTermAction || '-',
        '短期断点': issue.stBreakpoint || '-',
        '短期节点': issue.stBreakpointNode || '-',
        '长期措施': longTermActions.map(a => a.content).join('; ') || issue.longTermAction || '-',
        '长期断点': issue.ltBreakpoint || '-',
        '长期节点': issue.ltBreakpointNode || '-',
      };

      if (!isProduct) {
        baseData['问题类型'] = ISSUE_TYPE_LABELS_MAP[issue.type];
        baseData['风险等级'] = issue.riskStatus;
        baseData['问题日期'] = issue.openDate;
        baseData['车型节点'] = issue.vehicleNode || '-';
        baseData['工位'] = issue.station || '-';
        baseData['短期措施状态'] = shortTermActions.map(a => a.status).join('; ') || '-';
        baseData['长期措施状态'] = longTermActions.map(a => a.status).join('; ') || '-';
      }

      return baseData;
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    
    const sheetName = isProduct ? 'Product Issues' : 'Production Issues';
    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    const colWidths = isProduct
      ? [
          { wch: 8 },
          { wch: 40 },
          { wch: 12 },
          { wch: 12 },
          { wch: 20 },
          { wch: 40 },
          { wch: 12 },
          { wch: 10 },
          { wch: 40 },
          { wch: 12 },
          { wch: 10 },
          { wch: 50 },
        ]
      : [
          { wch: 8 },
          { wch: 12 },
          { wch: 12 },
          { wch: 40 },
          { wch: 12 },
          { wch: 10 },
          { wch: 12 },
          { wch: 12 },
          { wch: 10 },
          { wch: 10 },
          { wch: 20 },
          { wch: 40 },
          { wch: 15 },
          { wch: 12 },
          { wch: 10 },
          { wch: 40 },
          { wch: 15 },
          { wch: 12 },
          { wch: 10 },
          { wch: 50 },
        ];
    ws['!cols'] = colWidths;

    const dateStr = new Date().toISOString().split('T')[0];
    const fileName = `Issues_${activeTab}_${dateStr}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4 -mt-4">
          <div className="flex items-center gap-4">
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setActiveTab('product')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'product'
                    ? 'bg-white text-gray-800 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Product Issues ({productIssues.length})
              </button>
              <button
                onClick={() => setActiveTab('production')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'production'
                    ? 'bg-white text-gray-800 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Production Issues ({productionIssues.length})
              </button>
            </div>
          </div>

          <Dock
            items={[
              {
                icon: <Filter className="w-5 h-5" />,
                label: 'Filter',
                onClick: () => setShowFilters(!showFilters),
              },
              {
                icon: <Upload className="w-5 h-5" />,
                label: 'Import',
                onClick: () => setIsImportModalOpen(true),
              },
              {
                icon: <Download className="w-5 h-5" />,
                label: 'Export',
                onClick: exportIssuesToExcel,
              },
              {
                icon: <Plus className="w-5 h-5" />,
                label: 'New Issue',
                onClick: () => setIsCreateModalOpen(true),
                className: 'bg-blue-500 border-blue-400',
              },
            ]}
          />
        </div>

        {showFilters && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
                <div className="flex flex-wrap gap-1">
                  {(['0/4', '1/4', '2/4', '3/4', '4/4'] as const).map(status => (
                    <button
                      key={status}
                      onClick={() => {
                        const newStatus = filters.status.includes(status)
                          ? filters.status.filter(s => s !== status)
                          : [...filters.status, status];
                        setFilters({ ...filters, status: newStatus });
                      }}
                      className={`px-2 py-1 text-xs rounded ${
                        filters.status.includes(status)
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Issue Type</label>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(ISSUE_TYPE_LABELS).map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => {
                        const newTypes = filters.type.includes(key as any)
                          ? filters.type.filter(t => t !== key)
                          : [...filters.type, key as any];
                        setFilters({ ...filters, type: newTypes });
                      }}
                      className={`px-2 py-1 text-xs rounded ${
                        filters.type.includes(key as any)
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Keyword</label>
                <input
                  type="text"
                  value={filters.keyword}
                  onChange={(e) => setFilters({ ...filters, keyword: e.target.value })}
                  placeholder="Search description or owner..."
                  className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="flex items-end">
                <Button variant="ghost" size="sm" onClick={resetFilters}>
                  Reset
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-16 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  <div className="flex items-center gap-1">
                    <Hash className="w-3 h-3" />
                  </div>
                </th>
                <th className="w-20 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  <div className="flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    <span>Risk</span>
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  <div className="flex items-center gap-1">
                    <FileText className="w-3 h-3" />
                    <span>Description</span>
                  </div>
                </th>
                <th className="w-24 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  <div className="flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    <span>Progress</span>
                  </div>
                </th>
                <th className="w-28 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  <div className="flex items-center gap-1">
                    <User className="w-3 h-3" />
                    <span>Owner</span>
                  </div>
                </th>
                <th className="w-28 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  <div className="flex items-center gap-1">
                    <Settings className="w-3 h-3" />
                    <span>Actions</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredIssues.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    No issues found
                  </td>
                </tr>
              ) : (
                filteredIssues.map((issue) => (
                  <IssueRow
                    key={issue.id}
                    issue={issue}
                    editingIssue={editingIssue}
                    isExpanded={expandedRows.has(issue.id)}
                    onToggle={() => toggleRow(issue.id)}
                    onEdit={() => handleEditIssue(issue.id)}
                    onTaskComplete={handleTaskComplete}
                    isEditing={editingIssue?.id === issue.id}
                    onStartEdit={() => {
                      const latestIssue = issues.find(i => i.id === issue.id);
                      if (latestIssue) setEditingIssue(latestIssue);
                    }}
                    onCancelEdit={() => setEditingIssue(null)}
                    onUpdateEditingIssue={setEditingIssue}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </CardContent>

      <IssueCreateModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        issueType={activeTab}
      />

      <IssueImportModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        issueType={activeTab}
      />
    </Card>
  );
}

interface IssueRowProps {
  issue: Issue;
  editingIssue?: Issue | null;
  isExpanded: boolean;
  isEditing: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onTaskComplete: (taskId: string) => void;
  onUpdateEditingIssue?: (issue: Issue | null) => void;
  onStatusChange?: (status: string) => void;
}

function IssueRow({ issue, editingIssue, isExpanded, isEditing, onToggle, onEdit, onStartEdit, onCancelEdit, onTaskComplete, onUpdateEditingIssue, onStatusChange }: IssueRowProps) {
  const { deleteIssue, updateIssue, updateIssueLocally } = useIssueStore();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleStatusChange = (status: string) => {
    if (isEditing && onUpdateEditingIssue && editingIssue) {
      onUpdateEditingIssue({ ...editingIssue, status });
    } else if (onStatusChange) {
      onStatusChange(status);
    } else {
      updateIssueLocally(issue.id, { status });
      updateIssue(issue.id, { status }).catch((error) => {
        console.error('Failed to update status:', error);
      });
    }
  };

  const handleDelete = async () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    await deleteIssue(issue.id);
    setShowDeleteConfirm(false);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  return (
    <>
      <tr className="hover:bg-gray-50" data-issue-id={issue.id}>
        <td className="px-4 py-3 text-left">
          <span className="text-sm font-medium text-gray-600">
            {String(issue.sequence).padStart(3, '0')}
          </span>
        </td>
        <td className="px-4 py-3 text-center">
          <div
            className="w-3 h-3 rounded-full mx-auto"
            style={{ backgroundColor: RISK_STATUS_COLORS[issue.riskStatus] }}
            title={issue.riskStatus}
          />
        </td>
        <td className="px-4 py-3">
          <span className="text-sm text-gray-800 line-clamp-2">{issue.description}</span>
        </td>
        <td className="px-4 py-3 text-center">
          <ProgressBar 
            status={isEditing && editingIssue ? editingIssue.status : issue.status} 
            editable={isEditing}
            onChange={handleStatusChange}
          />
        </td>
        <td className="px-4 py-3 text-center">
          <span className="text-sm text-gray-600">{issue.owner || '-'}</span>
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-1">
            <button
              onClick={onToggle}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {isExpanded ? (
                <ChevronUp className="w-4 h-4 text-gray-500" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-500" />
              )}
            </button>
            {isEditing ? (
              <button
                onClick={onCancelEdit}
                className="p-1.5 bg-blue-100 hover:bg-blue-200 rounded-lg transition-colors"
                title="取消编辑"
              >
                <X className="w-4 h-4 text-blue-600" />
              </button>
            ) : (
              <button
                onClick={onEdit}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                title="编辑问题"
              >
                <Edit2 className="w-4 h-4 text-gray-500" />
              </button>
            )}
            <button
              onClick={handleDelete}
              className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
            >
              <Trash2 className="w-4 h-4 text-red-500" />
            </button>
          </div>
        </td>
      </tr>

      {isExpanded && (
        <tr>
          <td colSpan={6} className="px-4 py-4 bg-gray-50">
            <IssueDetail issue={issue} editingIssue={editingIssue} onTaskComplete={onTaskComplete} isEditing={isEditing} onStartEdit={onStartEdit} onCancelEdit={onCancelEdit} onUpdateEditingIssue={onUpdateEditingIssue} />
          </td>
        </tr>
      )}

      {showDeleteConfirm && (
        <tr>
          <td colSpan={6}>
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-4 max-w-sm w-full mx-4 shadow-xl">
                <p className="text-gray-800 mb-4">确定要删除这个问题吗？</p>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={cancelDelete}
                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
                  >
                    取消
                  </button>
                  <button
                    onClick={confirmDelete}
                    className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                  >
                    确定
                  </button>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

interface IssueDetailProps {
  issue: Issue;
  editingIssue?: Issue | null;
  isEditing: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onTaskComplete: (taskId: string) => void;
  onUpdateEditingIssue?: (issue: Issue | null) => void;
}

function IssueDetail({ issue, editingIssue, isEditing, onStartEdit, onCancelEdit, onTaskComplete, onUpdateEditingIssue }: IssueDetailProps) {
  const issueData = isEditing && editingIssue ? editingIssue : issue;
  const [expandedDelays, setExpandedDelays] = useState<Set<string>>(new Set());
  const [confirmConfig, setConfirmConfig] = useState<{ show: boolean; message: string; onConfirm: () => void } | null>(null);
  const { delayTask, updatePlanTask, updatePlanTaskLocally, updateIssue, updateIssueLocally, syncActionItems, addPlanTask, deletePlanTask, loadIssues, deleteActionItem } = useIssueStore();
  const { currentProject } = useProjectStore();

  const [editForm, setEditForm] = useState<{
    description: string;
    type: typeof issue.type;
    owner: string;
    rootCause: string;
    shortTermActions: EditFormActionItem[];
    longTermActions: EditFormActionItem[];
    planTasks: EditFormPlanTask[];
    attachments: Attachment[];
  }>(() => {
    const shortItems = issue.actionItems?.filter(a => a.actionType === 'short_term') || [];
    const longItems = issue.actionItems?.filter(a => a.actionType === 'long_term') || [];

    return {
      description: issue.description,
      type: issue.type,
      owner: issue.owner || '',
      rootCause: issue.rootCause || '',
      shortTermActions: shortItems.length > 0
        ? shortItems.map(a => ({
            id: a.id,
            content: a.content,
            breakpoint: a.breakpointDate || a.plannedDate || '',
            node: a.breakpointNode || '',
            status: a.status || 'pending',
            delayReason: a.delayReason || '',
            newPlannedDate: a.newPlannedDate || '',
            actualDate: a.actualDate || '',
            newBpNode: a.newBpNode || '',
            newBpReason: (a as any).newBpReason || '',
            newBpDate: (a as any).newBpDate || '',
            newBpNode2: (a as any).newBpNode2 || '',
            delayHistory: a.delayHistory || [],
          }))
        : [{ id: undefined, content: issue.shortTermAction || '', breakpoint: issue.stBreakpoint || '', node: issue.stBreakpointNode || '', status: 'pending' as const, delayReason: '', newPlannedDate: '', actualDate: '', newBpNode: '', newBpReason: '', newBpDate: '', newBpNode2: '', delayHistory: [] }],
      longTermActions: longItems.length > 0
        ? longItems.map(a => ({
            id: a.id,
            content: a.content,
            breakpoint: a.breakpointDate || a.plannedDate || '',
            node: a.breakpointNode || '',
            status: a.status || 'pending',
            delayReason: a.delayReason || '',
            newPlannedDate: a.newPlannedDate || '',
            actualDate: a.actualDate || '',
            newBpNode: a.newBpNode || '',
            newBpReason: (a as any).newBpReason || '',
            newBpDate: (a as any).newBpDate || '',
            newBpNode2: (a as any).newBpNode2 || '',
            delayHistory: a.delayHistory || [],
          }))
        : [{ id: undefined, content: issue.longTermAction || '', breakpoint: issue.ltBreakpoint || '', node: issue.ltBreakpointNode || '', status: 'pending' as const, delayReason: '', newPlannedDate: '', actualDate: '', newBpNode: '', newBpReason: '', newBpDate: '', newBpNode2: '', delayHistory: [] }],
      planTasks: issue.planTasks.map(t => ({
        id: t.id,
        content: t.content,
        plannedDate: t.plannedDate,
        status: t.status,
        delayReason: t.delayReason || '',
        newPlannedDate: t.newPlannedDate || '',
        actualDate: t.actualDate || '',
        delayHistory: t.delayHistory || [],
      })),
      attachments: issue.attachments || [],
    };
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isEditing) {
      const sourceIssue = editingIssue || issue;
      const shortItems = sourceIssue.actionItems?.filter(a => a.actionType === 'short_term') || [];
      const longItems = sourceIssue.actionItems?.filter(a => a.actionType === 'long_term') || [];

      setEditForm({
        description: sourceIssue.description,
        type: sourceIssue.type,
        owner: sourceIssue.owner || '',
        rootCause: sourceIssue.rootCause || '',
        shortTermActions: shortItems.length > 0
          ? shortItems.map(a => ({
              id: a.id,
              content: a.content,
              breakpoint: a.breakpointDate || a.plannedDate || '',
              node: a.breakpointNode || '',
              status: a.status || 'pending',
              delayReason: a.delayReason || '',
              newPlannedDate: a.newPlannedDate || '',
              actualDate: a.actualDate || '',
              newBpNode: a.newBpNode || '',
              newBpReason: (a as any).newBpReason || '',
              newBpDate: (a as any).newBpDate || '',
              newBpNode2: (a as any).newBpNode2 || '',
              delayHistory: a.delayHistory || [],
            }))
          : [{ id: undefined, content: sourceIssue.shortTermAction || '', breakpoint: sourceIssue.stBreakpoint || '', node: sourceIssue.stBreakpointNode || '', status: 'pending' as const, delayReason: '', newPlannedDate: '', actualDate: '', newBpNode: '', newBpReason: '', newBpDate: '', newBpNode2: '', delayHistory: [] }],
        longTermActions: longItems.length > 0
          ? longItems.map(a => ({
              id: a.id,
              content: a.content,
              breakpoint: a.breakpointDate || a.plannedDate || '',
              node: a.breakpointNode || '',
              status: a.status || 'pending',
              delayReason: a.delayReason || '',
              newPlannedDate: a.newPlannedDate || '',
              actualDate: a.actualDate || '',
              newBpNode: a.newBpNode || '',
              newBpReason: (a as any).newBpReason || '',
              newBpDate: (a as any).newBpDate || '',
              newBpNode2: (a as any).newBpNode2 || '',
              delayHistory: a.delayHistory || [],
            }))
          : [{ id: undefined, content: sourceIssue.longTermAction || '', breakpoint: sourceIssue.ltBreakpoint || '', node: sourceIssue.ltBreakpointNode || '', status: 'pending' as const, delayReason: '', newPlannedDate: '', actualDate: '', newBpNode: '', newBpReason: '', newBpDate: '', newBpNode2: '', delayHistory: [] }],
        planTasks: sourceIssue.planTasks.map(t => ({
          id: t.id,
          content: t.content,
          plannedDate: t.plannedDate,
          status: t.status,
          delayReason: t.delayReason || '',
          newPlannedDate: t.newPlannedDate || '',
          actualDate: t.actualDate || '',
          delayHistory: t.delayHistory || [],
        })),
        attachments: sourceIssue.attachments || [],
      });
    }
  }, [isEditing, editingIssue]);

  const addShortTermAction = () => {
    setEditForm(prev => ({
      ...prev,
      shortTermActions: [...prev.shortTermActions, { id: undefined, content: '', breakpoint: '', node: '', status: 'pending' as const, delayReason: '', newPlannedDate: '', actualDate: '', newBpNode: '', delayHistory: [] }]
    }));
  };

  const removeShortTermAction = (index: number) => {
    setEditForm(prev => ({
      ...prev,
      shortTermActions: prev.shortTermActions.filter((_, i) => i !== index)
    }));
  };

  const updateShortTermAction = (index: number, field: string, value: string) => {
    setEditForm(prev => ({
      ...prev,
      shortTermActions: prev.shortTermActions.map((a, i) => i === index ? { ...a, [field]: value } : a)
    }));
  };

  const addLongTermAction = () => {
    setEditForm(prev => ({
      ...prev,
      longTermActions: [...prev.longTermActions, { id: undefined, content: '', breakpoint: '', node: '', status: 'pending' as const, delayReason: '', newPlannedDate: '', actualDate: '', newBpNode: '', delayHistory: [] }]
    }));
  };

  const removeLongTermAction = (index: number) => {
    setEditForm(prev => ({
      ...prev,
      longTermActions: prev.longTermActions.filter((_, i) => i !== index)
    }));
  };

  const updateLongTermAction = (index: number, field: string, value: string) => {
    setEditForm(prev => ({
      ...prev,
      longTermActions: prev.longTermActions.map((a, i) => i === index ? { ...a, [field]: value } : a)
    }));
  };

  const addPlanTaskInEdit = () => {
    setEditForm(prev => ({
      ...prev,
      planTasks: [...prev.planTasks, { id: undefined, content: '', plannedDate: '', status: 'pending' as const, delayReason: '', newPlannedDate: '', actualDate: '', delayHistory: [], delayAction2: undefined, actualDate2: '' }]
    }));
  };

  const removePlanTask = (index: number) => {
    setEditForm(prev => ({
      ...prev,
      planTasks: prev.planTasks.filter((_, i) => i !== index)
    }));
  };

  const updatePlanTaskField = (index: number, field: string, value: string) => {
    setEditForm(prev => ({
      ...prev,
      planTasks: prev.planTasks.map((t, i) => i === index ? { ...t, [field]: value } : t)
    }));
  };

  const startEditing = () => {
    onStartEdit();
  };

  const saveIssue = async () => {
    if (!editForm.description.trim()) return;
    setIsSaving(true);

    const updates = {
      description: editForm.description,
      type: editForm.type,
      owner: editForm.owner || undefined,
      rootCause: editForm.rootCause || undefined,
      status: editingIssue?.status || issue.status,
      attachments: (editForm.attachments || []).map(a => ({
        name: a.name,
        url: a.url,
        type: a.type,
        size: a.size,
        uploadedAt: a.uploadedAt,
        uploadedBy: a.uploadedBy,
      })),
    };

    try {
      await updateIssue(issue.id, updates);

      const itemsToSync = [
        ...editForm.shortTermActions
          .filter(a => a.content.trim())
          .map((a, idx) => ({
            ...(a.id && { id: a.id }),
            issueId: issue.id,
            actionType: 'short_term' as const,
            content: a.content,
            plannedDate: a.breakpoint || undefined,
            breakpointDate: a.breakpoint || undefined,
            breakpointNode: a.node || undefined,
            sequence: idx + 1,
            status: a.status,
            delayReason: a.delayReason || undefined,
            newPlannedDate: a.newPlannedDate || undefined,
            newBpNode: a.newBpNode || undefined,
            actualDate: a.actualDate || undefined,
            newBpReason: a.newBpReason || undefined,
            newBpDate: a.newBpDate || undefined,
            newBpNode2: a.newBpNode2 || undefined,
          })),
        ...editForm.longTermActions
          .filter(a => a.content.trim())
          .map((a, idx) => ({
            ...(a.id && { id: a.id }),
            issueId: issue.id,
            actionType: 'long_term' as const,
            content: a.content,
            plannedDate: a.breakpoint || undefined,
            breakpointDate: a.breakpoint || undefined,
            breakpointNode: a.node || undefined,
            sequence: idx + 1,
            status: a.status,
            delayReason: a.delayReason || undefined,
            newPlannedDate: a.newPlannedDate || undefined,
            newBpNode: a.newBpNode || undefined,
            actualDate: a.actualDate || undefined,
            newBpReason: a.newBpReason || undefined,
            newBpDate: a.newBpDate || undefined,
            newBpNode2: a.newBpNode2 || undefined,
          })),
      ];

      const originalActionItemIds = issue.actionItems?.map(a => a.id) || [];
      const currentActionItemIds = editForm.shortTermActions.filter(a => a.id).map(a => a.id!)
        .concat(editForm.longTermActions.filter(a => a.id).map(a => a.id!));

      for (const originalAction of issue.actionItems || []) {
        if (!currentActionItemIds.includes(originalAction.id)) {
          await client.delete('/issues-action-items', { params: { id: originalAction.id } });
        }
      }

      for (let i = 0; i < editForm.shortTermActions.length; i++) {
        const action = editForm.shortTermActions[i];
        if (action.id && action.content.trim()) {
          const originalAction = issueData.actionItems?.find(a => a.id === action.id);
          if (originalAction) {
            const statusChanged = originalAction.status !== action.status;
            const isBpNg = action.status === 'bp-ng';
            const isDelayedComplete = action.status === 'delayed-complete';
            const isPendingOrCompleted = action.status === 'pending' || action.status === 'completed';

            if (isPendingOrCompleted && statusChanged) {
              if (originalAction.delayHistory && originalAction.delayHistory.length > 0) {
                for (const record of originalAction.delayHistory) {
                  await client.delete(`/issues-action-delays?id=${record.id}`);
                }
              }
              await client.patch(`/issues-action-items/${action.id}/clear-delay`);
            }

            const shouldSaveDelay = (isBpNg || isDelayedComplete) && action.delayReason && action.newPlannedDate;
            const hasNewDelay = action.expandBpNg === 'bp-again' && action.newBpReason && action.newBpDate;
            const delayReasonChanged = originalAction.delayReason !== action.delayReason;
            const newPlannedDateChanged = originalAction.newPlannedDate !== action.newPlannedDate;
            const shouldCreateDelay = (statusChanged && shouldSaveDelay) || hasNewDelay || (shouldSaveDelay && (delayReasonChanged || newPlannedDateChanged));
            if (shouldCreateDelay) {
              const originalPlannedDate = action.expandBpNg === 'bp-again'
                ? action.newPlannedDate
                : (action.breakpointDate || action.plannedDate || originalAction.plannedDate);
              const newPlannedDateToSave = action.expandBpNg === 'bp-again' ? action.newBpDate : action.newPlannedDate;
              const bpNodeToSave = action.expandBpNg === 'bp-again' ? action.newBpNode2 : action.newBpNode;
              const delayReasonToSave = action.expandBpNg === 'bp-again' ? action.newBpReason : action.delayReason;
              if (newPlannedDateToSave && delayReasonToSave) {
                await client.post('/issues-action-delays', {
                  actionItemId: action.id,
                  originalPlannedDate: originalPlannedDate,
                  newPlannedDate: newPlannedDateToSave,
                  bpNode: bpNodeToSave || '',
                  delayReason: delayReasonToSave,
                  isBpNg: isBpNg,
                });
              }
            }
          }
        }
      }

      for (let i = 0; i < editForm.longTermActions.length; i++) {
        const action = editForm.longTermActions[i];
        if (action.id && action.content.trim()) {
          const originalAction = issueData.actionItems?.find(a => a.id === action.id);
          if (originalAction) {
            const statusChanged = originalAction.status !== action.status;
            const isBpNg = action.status === 'bp-ng';
            const isDelayedComplete = action.status === 'delayed-complete';
            const isPendingOrCompleted = action.status === 'pending' || action.status === 'completed';

            if (isPendingOrCompleted && statusChanged) {
              if (originalAction.delayHistory && originalAction.delayHistory.length > 0) {
                for (const record of originalAction.delayHistory) {
                  await client.delete(`/issues-action-delays?id=${record.id}`);
                }
              }
              await client.patch(`/issues-action-items/${action.id}/clear-delay`);
            }

            const shouldSaveDelay = (isBpNg || isDelayedComplete) && action.delayReason && action.newPlannedDate;
            const hasNewDelay = action.expandBpNg === 'bp-again' && action.newBpReason && action.newBpDate;
            const delayReasonChanged = originalAction.delayReason !== action.delayReason;
            const newPlannedDateChanged = originalAction.newPlannedDate !== action.newPlannedDate;
            const shouldCreateDelay = (statusChanged && shouldSaveDelay) || hasNewDelay || (shouldSaveDelay && (delayReasonChanged || newPlannedDateChanged));
            if (shouldCreateDelay) {
              const originalPlannedDate = action.expandBpNg === 'bp-again'
                ? action.newPlannedDate
                : (action.breakpointDate || action.plannedDate || originalAction.plannedDate);
              const newPlannedDateToSave = action.expandBpNg === 'bp-again' ? action.newBpDate : action.newPlannedDate;
              const bpNodeToSave = action.expandBpNg === 'bp-again' ? action.newBpNode2 : action.newBpNode;
              const delayReasonToSave = action.expandBpNg === 'bp-again' ? action.newBpReason : action.delayReason;
              if (newPlannedDateToSave && delayReasonToSave) {
                await client.post('/issues-action-delays', {
                  actionItemId: action.id,
                  originalPlannedDate: originalPlannedDate,
                  newPlannedDate: newPlannedDateToSave,
                  bpNode: bpNodeToSave || '',
                  delayReason: delayReasonToSave,
                  isBpNg: isBpNg,
                });
              }
            }
          }
        }
      }

      const originalTaskIds = issue.planTasks.map(t => t.id);
      const currentTaskIds = editForm.planTasks.filter(t => t.id).map(t => t.id);

      for (const originalTask of issue.planTasks) {
        if (!currentTaskIds.includes(originalTask.id)) {
          await deletePlanTask(originalTask.id);
        }
      }

      for (let i = 0; i < editForm.planTasks.length; i++) {
        const task = editForm.planTasks[i];

        if (task.id && task.content.trim()) {
          const originalTask = issue.planTasks.find(t => t.id === task.id);
          if (originalTask) {
            const statusChanged = originalTask.status !== task.status;
            const contentChanged = originalTask.content !== task.content;
            const dateChanged = originalTask.plannedDate !== task.plannedDate;
            const delayReasonChanged = originalTask.delayReason !== task.delayReason;
            const actualDateChanged = originalTask.actualDate !== task.actualDate;
            const newPlannedDateChanged = originalTask.newPlannedDate !== task.newPlannedDate;
            
            const isDelayed = task.status === 'delayed';
            const isDelayedCompleted = task.status === 'delayed-completed';
            const hasDelayAction2 = task.delayAction === 'delay-again' && (task.newDelayReason || task.newDelayPlannedDate);
            const shouldSaveDelay = isDelayed 
              ? (delayReasonChanged || newPlannedDateChanged || hasDelayAction2)
              : isDelayedCompleted 
                ? (delayReasonChanged || actualDateChanged)
                : false;
            const shouldSave = statusChanged || contentChanged || dateChanged || shouldSaveDelay;
            if (shouldSave) {
              const updateData: any = {
                content: task.content,
                plannedDate: task.plannedDate,
                status: task.status,
              };
              
              if (task.status === 'completed') {
                updateData.actualDate = new Date().toISOString().split('T')[0];
                updateData.newPlannedDate = null;
                updateData.delayReason = null;
                updateData.delayHistory = [];
              } else if (task.status === 'delayed') {
                updateData.delayReason = task.delayReason || null;
                updateData.newPlannedDate = task.newPlannedDate || null;
                updateData.actualDate = null;
                
                if (task.delayAction === 'completed' && task.actualDate) {
                  updateData.status = 'delayed-completed';
                  updateData.actualDate = task.actualDate;
                } else if (task.delayAction === 'delay-again' && task.newDelayReason && task.newDelayPlannedDate) {
                  const newDelayRecord = {
                    id: `delay-${Date.now()}`,
                    taskId: task.id,
                    originalPlannedDate: task.newPlannedDate,
                    newPlannedDate: task.newDelayPlannedDate,
                    delayReason: task.newDelayReason,
                    recordedAt: new Date().toISOString(),
                    recordedBy: 'current-user',
                  };
                  const updatedDelayHistory = [...(task.delayHistory || []), newDelayRecord];
                  updateData.delayHistory = updatedDelayHistory;
                  updateData.newPlannedDate = task.newDelayPlannedDate;
                  updateData.delayReason = task.newDelayReason;
                } else if (task.delayReason && task.newPlannedDate) {
                  const newDelayRecord = {
                    id: `delay-${Date.now()}`,
                    taskId: task.id,
                    originalPlannedDate: originalTask.plannedDate,
                    newPlannedDate: task.newPlannedDate,
                    delayReason: task.delayReason,
                    recordedAt: new Date().toISOString(),
                    recordedBy: 'current-user',
                  };
                  updateData.delayHistory = [...(originalTask.delayHistory || []), newDelayRecord];
                }
              } else if (task.status === 'delayed-completed') {
                updateData.delayReason = task.delayReason || originalTask.delayReason || null;
                updateData.newPlannedDate = null;
                updateData.actualDate = task.actualDate || new Date().toISOString().split('T')[0];
                
                const currentDelayReason = task.delayReason || originalTask.delayReason;
                const currentDelayRecord = {
                  id: `delay-${Date.now()}`,
                  taskId: task.id,
                  originalPlannedDate: originalTask.plannedDate,
                  newPlannedDate: task.actualDate || new Date().toISOString().split('T')[0],
                  delayReason: currentDelayReason,
                  recordedAt: new Date().toISOString(),
                  recordedBy: 'current-user',
                };
                updateData.delayHistory = [...(originalTask.delayHistory || []), currentDelayRecord];
              } else if (task.status === 'pending') {
                updateData.actualDate = null;
                updateData.newPlannedDate = null;
                updateData.delayReason = null;
                updateData.delayHistory = [];
              }
              
              await updatePlanTask(task.id, updateData);
            }
          }
        } else if (!task.id && task.content.trim()) {
          const newTaskData: any = {
            content: task.content,
            plannedDate: task.plannedDate || null,
            status: task.status,
          };
          
          if (task.status === 'completed') {
            newTaskData.actualDate = new Date().toISOString().split('T')[0];
          } else if (task.status === 'delayed' || task.status === 'delayed-completed') {
            newTaskData.delayReason = task.delayReason || null;
            newTaskData.newPlannedDate = task.newPlannedDate || null;
            if (task.status === 'delayed-completed') {
              newTaskData.actualDate = task.actualDate || new Date().toISOString().split('T')[0];
            }
          }
          
          await addPlanTask(issue.id, newTaskData);
        }
      }

      await syncActionItems(issue.id, itemsToSync);

      onCancelEdit();
      if (currentProject) {
        await loadIssues(currentProject.id);
      }
    } catch (error) {
      console.error('Failed to save issue:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const showConfirmDialog = (message: string, onConfirm: () => void) => {
    setConfirmConfig({ show: true, message, onConfirm });
  };

  const handleConfirm = () => {
    if (confirmConfig?.onConfirm) {
      confirmConfig.onConfirm();
    }
    setConfirmConfig(null);
  };

  const handleCancel = () => {
    setConfirmConfig(null);
  };

  const toggleDelayExpand = (taskId: string) => {
    const newExpanded = new Set(expandedDelays);
    if (newExpanded.has(taskId)) {
      newExpanded.delete(taskId);
    } else {
      newExpanded.add(taskId);
    }
    setExpandedDelays(newExpanded);
  };

  return (
    <div className="py-2 space-y-2">
      {isEditing ? (
        <div className="bg-gray-100 p-2 rounded border border-gray-300">
          <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-300">
            <span className="text-xs font-medium text-gray-700">编辑 #{String(issue.sequence).padStart(3, '0')}</span>
            <div className="flex gap-2">
              <button
                onClick={onCancelEdit}
                className="px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-100 rounded"
              >
                取消
              </button>
              <button
                onClick={saveIssue}
                disabled={!editForm.description.trim() || isSaving}
                className="px-2 py-0.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300"
              >
                {isSaving ? '保存中...' : '保存'}
              </button>
            </div>
          </div>

          <div className="space-y-2 text-xs">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <span className="text-gray-500">类型:</span>
                <select
                  value={editForm.type}
                  onChange={(e) => setEditForm(prev => ({ ...prev, type: e.target.value as any }))}
                  className="px-1 py-0.5 border border-gray-300 rounded text-xs"
                >
                  {(['dimension', 'appearance', 'function', 'other'] as const).map((type) => (
                    <option key={type} value={type}>{ISSUE_TYPE_LABELS[type]}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-1">
                <span className="text-gray-500">描述:</span>
                <input
                  type="text"
                  value={editForm.description}
                  onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                  className="px-1 py-0.5 border border-gray-300 rounded text-xs w-48"
                />
              </div>

              <div className="flex items-center gap-1">
                <span className="text-gray-500">责任人:</span>
                <input
                  type="text"
                  value={editForm.owner}
                  onChange={(e) => setEditForm(prev => ({ ...prev, owner: e.target.value }))}
                  className="px-1 py-0.5 border border-gray-300 rounded text-xs w-20"
                />
              </div>
            </div>

            <div className="flex items-center gap-1">
              <span className="text-gray-500">原因:</span>
              <input
                type="text"
                value={editForm.rootCause}
                onChange={(e) => setEditForm(prev => ({ ...prev, rootCause: e.target.value }))}
                className="flex-1 px-1 py-0.5 border border-gray-300 rounded text-xs"
              />
            </div>

            <div>
              <div className="flex items-center gap-1 mb-1">
                <span className="text-gray-500">短期措施:</span>
                <button
                  onClick={addShortTermAction}
                  className="px-1 py-0.5 text-xs text-blue-600 hover:bg-blue-50 rounded"
                >
                  + 添加
                </button>
              </div>
              {editForm.shortTermActions.map((action, idx) => (
                <div key={idx} className="mb-2 last:mb-0">
                  <div className="flex items-center gap-1 mb-1">
                    <input
                      type="text"
                      value={action.content}
                      onChange={(e) => updateShortTermAction(idx, 'content', e.target.value)}
                      placeholder="措施内容"
                      className="flex-1 px-1 py-0.5 border border-gray-300 rounded text-xs"
                    />
                    <input
                      type="date"
                      value={action.breakpoint}
                      onChange={(e) => updateShortTermAction(idx, 'breakpoint', e.target.value)}
                      placeholder="断点时间"
                      className="px-1 py-0.5 border border-gray-300 rounded text-xs w-28"
                    />
                    <input
                      type="text"
                      value={action.node}
                      onChange={(e) => updateShortTermAction(idx, 'node', e.target.value)}
                      placeholder="断点节点"
                      className="px-1 py-0.5 border border-gray-300 rounded text-xs w-20"
                    />
                    <select
                      value={action.status}
                      onChange={(e) => updateShortTermAction(idx, 'status', e.target.value)}
                      className="px-1 py-0.5 border border-gray-300 rounded text-xs"
                    >
                      <option value="pending">待执行</option>
                      <option value="completed">已完成</option>
                      <option value="bp-ng">BP失效</option>
                      <option value="delayed-complete">延期完成</option>
                    </select>
                    {editForm.shortTermActions.length > 1 && (
                      <button
                        onClick={() => removeShortTermAction(idx)}
                        className="p-1 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    )}
                  </div>
                  {action.status === 'delayed-complete' && (
                    <div className="ml-1 mt-1 p-2 rounded">
                      {action.delayHistory && action.delayHistory.length > 0 && (
                        <div className="space-y-1 mb-2">
                          {action.delayHistory.map((record, rIdx) => (
                            <div key={rIdx} className="flex items-center gap-2 text-xs text-gray-500 p-1 rounded">
                              <span className="text-orange-500">延期{rIdx + 1}:</span>
                              <span className="flex-1">{record.delayReason} → {record.newPlannedDate}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 w-16">延期原因:</span>
                        <input
                          type="text"
                          value={action.delayReason}
                          onChange={(e) => updateShortTermAction(idx, 'delayReason', e.target.value)}
                          placeholder="请输入延期原因"
                          className="flex-1 px-1 py-0.5 border border-gray-300 rounded text-xs"
                        />
                        <span className="text-xs text-gray-500 w-16">实际断点:</span>
                        <input
                          type="date"
                          value={action.newPlannedDate}
                          onChange={(e) => updateShortTermAction(idx, 'newPlannedDate', e.target.value)}
                          className="px-1 py-0.5 border border-gray-300 rounded text-xs"
                        />
                        <input
                          type="text"
                          value={action.newBpNode}
                          onChange={(e) => updateShortTermAction(idx, 'newBpNode', e.target.value)}
                          placeholder="节点"
                          className="px-1 py-0.5 border border-gray-300 rounded text-xs w-20"
                        />
                      </div>
                    </div>
                  )}
                  {action.status === 'bp-ng' && (
                    <div className="ml-1 mt-1 p-2 rounded">
                      {action.delayHistory && action.delayHistory.length > 0 && (
                        <div className="space-y-1 mb-2">
                          {action.delayHistory.map((record, rIdx) => (
                            <div key={rIdx} className="flex items-center gap-2 text-xs text-gray-500 p-1 rounded">
                              <span className="text-orange-500">延期{rIdx + 1}:</span>
                              <span className="flex-1">{record.delayReason} → {record.newPlannedDate}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 w-16">失效原因:</span>
                        <input
                          type="text"
                          value={action.delayReason}
                          onChange={(e) => updateShortTermAction(idx, 'delayReason', e.target.value)}
                          placeholder="请输入BP失效原因"
                          className="flex-1 px-1 py-0.5 border border-gray-300 rounded text-xs"
                        />
                        <span className="text-xs text-gray-500 w-16">新断点:</span>
                        <input
                          type="date"
                          value={action.newPlannedDate}
                          onChange={(e) => updateShortTermAction(idx, 'newPlannedDate', e.target.value)}
                          className="px-1 py-0.5 border border-gray-300 rounded text-xs"
                        />
                        <input
                          type="text"
                          value={action.newBpNode}
                          onChange={(e) => updateShortTermAction(idx, 'newBpNode', e.target.value)}
                          placeholder="节点"
                          className="px-1 py-0.5 border border-gray-300 rounded text-xs w-20"
                        />
                        <button
                          onClick={() => {
                            updateShortTermAction(idx, 'expandBpNg', 'bp-again');
                            updateShortTermAction(idx, 'expandBpNg2', undefined);
                            updateShortTermAction(idx, 'actualDate2', '');
                          }}
                          className={`px-2 py-0.5 text-xs rounded ${action.expandBpNg === 'bp-again' ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                        >
                          再次失效
                        </button>
                      </div>
                      {action.expandBpNg === 'bp-again' && action.delayReason && action.newPlannedDate && (
                        <div className="mt-2 p-2 border border-red-200 rounded space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-red-600 font-medium">再次失效:</span>
                            <span className="text-xs text-gray-500 w-16">失效原因:</span>
                            <input
                              type="text"
                              value={action.newBpReason || ''}
                              onChange={(e) => updateShortTermAction(idx, 'newBpReason', e.target.value)}
                              placeholder="请输入新的失效原因"
                              className="flex-1 px-1 py-0.5 border border-gray-300 rounded text-xs"
                            />
                            <span className="text-xs text-gray-500 w-16">新断点:</span>
                            <input
                              type="date"
                              value={action.newBpDate || ''}
                              onChange={(e) => updateShortTermAction(idx, 'newBpDate', e.target.value)}
                              className="px-1 py-0.5 border border-gray-300 rounded text-xs"
                            />
                            <input
                              type="text"
                              value={action.newBpNode2 || ''}
                              onChange={(e) => updateShortTermAction(idx, 'newBpNode2', e.target.value)}
                              placeholder="节点"
                              className="px-1 py-0.5 border border-gray-300 rounded text-xs w-20"
                            />
                          </div>
                          {action.expandBpNg2 === 'completed' && (
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-gray-500 w-16">实际完成:</span>
                              <input
                                type="date"
                                value={action.actualDate2 || ''}
                                onChange={(e) => updateShortTermAction(idx, 'actualDate2', e.target.value)}
                                className="px-1 py-0.5 border border-gray-300 rounded text-xs"
                              />
                            </div>
                          )}
                        </div>
                      )}
                      {action.expandBpNg === 'completed' && (
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-gray-500 w-16">实际完成:</span>
                          <input
                            type="date"
                            value={action.actualDate}
                            onChange={(e) => updateShortTermAction(idx, 'actualDate', e.target.value)}
                            className="px-1 py-0.5 border border-gray-300 rounded text-xs"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div>
              <div className="flex items-center gap-1 mb-1">
                <span className="text-gray-500">长期措施:</span>
                <button
                  onClick={addLongTermAction}
                  className="px-1 py-0.5 text-xs text-blue-600 hover:bg-blue-50 rounded"
                >
                  + 添加
                </button>
              </div>
              {editForm.longTermActions.map((action, idx) => (
                <div key={idx} className="mb-2 last:mb-0">
                  <div className="flex items-center gap-1 mb-1">
                    <input
                      type="text"
                      value={action.content}
                      onChange={(e) => updateLongTermAction(idx, 'content', e.target.value)}
                      placeholder="措施内容"
                      className="flex-1 px-1 py-0.5 border border-gray-300 rounded text-xs"
                    />
                    <input
                      type="date"
                      value={action.breakpoint}
                      onChange={(e) => updateLongTermAction(idx, 'breakpoint', e.target.value)}
                      placeholder="断点时间"
                      className="px-1 py-0.5 border border-gray-300 rounded text-xs w-28"
                    />
                    <input
                      type="text"
                      value={action.node}
                      onChange={(e) => updateLongTermAction(idx, 'node', e.target.value)}
                      placeholder="断点节点"
                      className="px-1 py-0.5 border border-gray-300 rounded text-xs w-20"
                    />
                    <select
                      value={action.status}
                      onChange={(e) => updateLongTermAction(idx, 'status', e.target.value)}
                      className="px-1 py-0.5 border border-gray-300 rounded text-xs"
                    >
                      <option value="pending">待执行</option>
                      <option value="completed">已完成</option>
                      <option value="bp-ng">BP失效</option>
                      <option value="delayed-complete">延期完成</option>
                    </select>
                    {editForm.longTermActions.length > 1 && (
                      <button
                        onClick={() => removeLongTermAction(idx)}
                        className="p-1 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    )}
                  </div>
                  {action.status === 'delayed-complete' && (
                    <div className="ml-1 mt-1 p-2 rounded">
                      {action.delayHistory && action.delayHistory.length > 0 && (
                        <div className="space-y-1 mb-2">
                          {action.delayHistory.map((record, rIdx) => (
                            <div key={rIdx} className="flex items-center gap-2 text-xs text-gray-500 p-1 rounded">
                              <span className="text-orange-500">延期{rIdx + 1}:</span>
                              <span className="flex-1">{record.delayReason} → {record.newPlannedDate}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 w-16">延期原因:</span>
                        <input
                          type="text"
                          value={action.delayReason}
                          onChange={(e) => updateLongTermAction(idx, 'delayReason', e.target.value)}
                          placeholder="请输入延期原因"
                          className="flex-1 px-1 py-0.5 border border-gray-300 rounded text-xs"
                        />
                        <span className="text-xs text-gray-500 w-16">实际断点:</span>
                        <input
                          type="date"
                          value={action.newPlannedDate}
                          onChange={(e) => updateLongTermAction(idx, 'newPlannedDate', e.target.value)}
                          className="px-1 py-0.5 border border-gray-300 rounded text-xs"
                        />
                        <input
                          type="text"
                          value={action.newBpNode}
                          onChange={(e) => updateLongTermAction(idx, 'newBpNode', e.target.value)}
                          placeholder="节点"
                          className="px-1 py-0.5 border border-gray-300 rounded text-xs w-20"
                        />
                      </div>
                    </div>
                  )}
                  {action.status === 'bp-ng' && (
                    <div className="ml-1 mt-1 p-2 rounded">
                      {action.delayHistory && action.delayHistory.length > 0 && (
                        <div className="space-y-1 mb-2">
                          {action.delayHistory.map((record, rIdx) => (
                            <div key={rIdx} className="flex items-center gap-2 text-xs text-gray-500 p-1 rounded">
                              <span className="text-orange-500">延期{rIdx + 1}:</span>
                              <span className="flex-1">{record.delayReason} → {record.newPlannedDate}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 w-16">失效原因:</span>
                        <input
                          type="text"
                          value={action.delayReason}
                          onChange={(e) => updateLongTermAction(idx, 'delayReason', e.target.value)}
                          placeholder="请输入BP失效原因"
                          className="flex-1 px-1 py-0.5 border border-gray-300 rounded text-xs"
                        />
                        <span className="text-xs text-gray-500 w-16">新断点:</span>
                        <input
                          type="date"
                          value={action.newPlannedDate}
                          onChange={(e) => updateLongTermAction(idx, 'newPlannedDate', e.target.value)}
                          className="px-1 py-0.5 border border-gray-300 rounded text-xs"
                        />
                        <input
                          type="text"
                          value={action.newBpNode}
                          onChange={(e) => updateLongTermAction(idx, 'newBpNode', e.target.value)}
                          placeholder="节点"
                          className="px-1 py-0.5 border border-gray-300 rounded text-xs w-20"
                        />
                        <button
                          onClick={() => {
                            updateLongTermAction(idx, 'expandBpNg', 'bp-again');
                            updateLongTermAction(idx, 'expandBpNg2', undefined);
                            updateLongTermAction(idx, 'actualDate2', '');
                          }}
                          className={`px-2 py-0.5 text-xs rounded ${action.expandBpNg === 'bp-again' ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                        >
                          再次失效
                        </button>
                      </div>
                      {action.expandBpNg === 'bp-again' && action.delayReason && action.newPlannedDate && (
                        <div className="mt-2 p-2 border border-red-200 rounded space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-red-600 font-medium">再次失效:</span>
                            <span className="text-xs text-gray-500 w-16">失效原因:</span>
                            <input
                              type="text"
                              value={action.newBpReason || ''}
                              onChange={(e) => updateLongTermAction(idx, 'newBpReason', e.target.value)}
                              placeholder="请输入新的失效原因"
                              className="flex-1 px-1 py-0.5 border border-gray-300 rounded text-xs"
                            />
                            <span className="text-xs text-gray-500 w-16">新断点:</span>
                            <input
                              type="date"
                              value={action.newBpDate || ''}
                              onChange={(e) => updateLongTermAction(idx, 'newBpDate', e.target.value)}
                              className="px-1 py-0.5 border border-gray-300 rounded text-xs"
                            />
                            <input
                              type="text"
                              value={action.newBpNode2 || ''}
                              onChange={(e) => updateLongTermAction(idx, 'newBpNode2', e.target.value)}
                              placeholder="节点"
                              className="px-1 py-0.5 border border-gray-300 rounded text-xs w-20"
                            />
                          </div>
                          {action.expandBpNg2 === 'completed' && (
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-gray-500 w-16">实际完成:</span>
                              <input
                                type="date"
                                value={action.actualDate2 || ''}
                                onChange={(e) => updateLongTermAction(idx, 'actualDate2', e.target.value)}
                                className="px-1 py-0.5 border border-gray-300 rounded text-xs"
                              />
                            </div>
                          )}
                        </div>
                      )}
                      {action.expandBpNg === 'completed' && (
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-gray-500 w-16">实际完成:</span>
                          <input
                            type="date"
                            value={action.actualDate}
                            onChange={(e) => updateLongTermAction(idx, 'actualDate', e.target.value)}
                            className="px-1 py-0.5 border border-gray-300 rounded text-xs"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div>
              <div className="flex items-center gap-1 mb-1">
                <span className="text-gray-500">工作计划:</span>
                <button
                  onClick={addPlanTaskInEdit}
                  className="px-1 py-0.5 text-xs text-blue-600 hover:bg-blue-50 rounded"
                >
                  + 添加
                </button>
              </div>
              {editForm.planTasks.length > 0 && (
                <div className="p-2 bg-gray-50 rounded border border-gray-200">
                  {editForm.planTasks.map((task, idx) => (
                    <div key={idx} className="mb-2 last:mb-0">
                      <div className="flex items-center gap-1 mb-1">
                        <input
                          type="text"
                          value={task.content}
                          onChange={(e) => updatePlanTaskField(idx, 'content', e.target.value)}
                          placeholder="任务内容"
                          className="flex-1 px-1 py-0.5 border border-gray-300 rounded text-xs"
                        />
                        <input
                          type="date"
                          value={task.plannedDate}
                          onChange={(e) => updatePlanTaskField(idx, 'plannedDate', e.target.value)}
                          className="px-1 py-0.5 border border-gray-300 rounded text-xs w-28"
                        />
                        <select
                          value={task.status}
                          onChange={(e) => updatePlanTaskField(idx, 'status', e.target.value)}
                          className="px-1 py-0.5 border border-gray-300 rounded text-xs"
                        >
                          <option value="pending">待执行</option>
                          <option value="completed">已完成</option>
                          <option value="delayed">已延期</option>
                          <option value="delayed-completed">延期完成</option>
                        </select>
                        <button
                          onClick={() => removePlanTask(idx)}
                          className="p-1 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                      {task.status === 'delayed' && (
                        <div className="ml-1 mt-1 p-2 space-y-1">
                          {task.delayHistory && task.delayHistory.length > 0 && (
                            <div className="space-y-1 mb-2">
                              {task.delayHistory.map((record, rIdx) => (
                                <div key={rIdx} className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 p-1 rounded">
                                  <span className="text-orange-500">延期{record.delayReason ? '' : ''}{rIdx + 1}:</span>
                                  <span className="flex-1">{record.delayReason} → {record.newPlannedDate}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500 w-16">延期原因:</span>
                            <input
                              type="text"
                              value={task.delayReason}
                              onChange={(e) => updatePlanTaskField(idx, 'delayReason', e.target.value)}
                              placeholder="请输入延期原因"
                              className="flex-1 px-1 py-0.5 border border-gray-300 rounded text-xs"
                            />
                            <span className="text-xs text-gray-500 w-16">新计划时间:</span>
                            <input
                              type="date"
                              value={task.newPlannedDate}
                              onChange={(e) => updatePlanTaskField(idx, 'newPlannedDate', e.target.value)}
                              className="px-1 py-0.5 border border-gray-300 rounded text-xs"
                            />
                            <button
                              onClick={() => {
                                updatePlanTaskField(idx, 'delayAction', 'delay-again');
                                updatePlanTaskField(idx, 'delayAction2', undefined);
                                updatePlanTaskField(idx, 'actualDate2', '');
                              }}
                              className={`px-2 py-0.5 text-xs rounded ${task.delayAction === 'delay-again' ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                            >
                              再次延期
                            </button>
                          </div>
                          {task.delayAction === 'delay-again' && task.delayReason && task.newPlannedDate && (
                            <div className="mt-2 p-2 border border-orange-200 rounded space-y-1">
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-orange-600 font-medium">再次延期:</span>
                                <span className="text-xs text-gray-500 w-16">延期原因:</span>
                                <input
                                  type="text"
                                  value={task.newDelayReason || ''}
                                  onChange={(e) => updatePlanTaskField(idx, 'newDelayReason', e.target.value)}
                                  placeholder="请输入新的延期原因"
                                  className="flex-1 px-1 py-0.5 border border-gray-300 rounded text-xs"
                                />
                                <span className="text-xs text-gray-500 w-16">新计划时间:</span>
                                <input
                                  type="date"
                                  value={task.newDelayPlannedDate || ''}
                                  onChange={(e) => updatePlanTaskField(idx, 'newDelayPlannedDate', e.target.value)}
                                  className="px-1 py-0.5 border border-gray-300 rounded text-xs"
                                />
                              </div>
                              {task.delayAction2 === 'completed' && (
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-xs text-gray-500 w-16">实际完成时间:</span>
                                  <input
                                    type="date"
                                    value={task.actualDate2 || ''}
                                    onChange={(e) => updatePlanTaskField(idx, 'actualDate2', e.target.value)}
                                    className="px-1 py-0.5 border border-gray-300 rounded text-xs"
                                  />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      {task.status === 'delayed-completed' && (
                        <div className="ml-1 mt-1 p-2 space-y-1">
                          <div className="text-xs font-medium text-gray-700">延期完成信息</div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500 w-16">延期原因:</span>
                            <input
                              type="text"
                              value={task.delayReason}
                              onChange={(e) => updatePlanTaskField(idx, 'delayReason', e.target.value)}
                              placeholder="请输入延期原因"
                              className="flex-1 px-1 py-0.5 border border-gray-300 rounded text-xs"
                            />
                            <span className="text-xs text-gray-500 w-16">实际完成:</span>
                            <input
                              type="date"
                              value={task.actualDate}
                              onChange={(e) => updatePlanTaskField(idx, 'actualDate', e.target.value)}
                              className="px-1 py-0.5 border border-gray-300 rounded text-xs"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-1 mt-2">
                <span className="text-gray-500">附件管理:</span>
                <label className="cursor-pointer px-1 py-0.5 text-xs text-blue-600 hover:bg-blue-50 rounded">
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                    onChange={async (e) => {
                      const files = e.target.files;
                      if (!files) return;
                      const newAttachments: Attachment[] = [];
                      for (let i = 0; i < files.length; i++) {
                        const file = files[i];
                        const formDataFile = new FormData();
                        formDataFile.append('file', file);
                        try {
                          const response = await fetch('/api/upload', {
                            method: 'POST',
                            body: formDataFile,
                          });
                          const data = await response.json();
                          if (data.url) {
                            newAttachments.push({
                              id: crypto.randomUUID(),
                              issueId: issue.id,
                              name: file.name,
                              url: data.url,
                              type: file.type,
                              size: file.size,
                              uploadedAt: new Date().toISOString(),
                              uploadedBy: 'currentUser',
                            });
                          }
                        } catch (error) {
                          console.error('上传失败:', error);
                        }
                      }
                      setEditForm(prev => ({
                        ...prev,
                        attachments: [...(prev.attachments || []), ...newAttachments],
                      }));
                    }}
                  />
                  + 添加
                </label>
                {(editForm.attachments || []).length > 0 && (
                  <div className="flex items-center gap-1 flex-wrap">
                    {(editForm.attachments || []).map((att) => (
                      <div key={att.id} className="flex items-center gap-1 bg-gray-50 px-1 py-0.5 rounded">
                        <a
                          href={att.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-500 hover:text-blue-700 hover:underline"
                        >
                          {att.name}
                        </a>
                        <button
                          onClick={async () => {
                            try {
                              await fetch(`/api/upload?url=${encodeURIComponent(att.url)}`, {
                                method: 'DELETE',
                              });
                            } catch (error) {
                              console.error('删除文件失败:', error);
                            }
                            setEditForm(prev => ({
                              ...prev,
                              attachments: (prev.attachments || []).filter(a => a.id !== att.id),
                            }));
                          }}
                          className="text-xs text-gray-400 hover:text-red-500"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <div>
            <span className="text-xs font-bold text-gray-500">基础信息：</span>
            <span className="text-xs text-gray-700 ml-1">
              {ISSUE_TYPE_LABELS[issue.type]} | {issue.openDate}
              {issue.vehicleNode && ` | ${issue.vehicleNode}`}
              {issue.station && ` | ${issue.station}`}
            </span>
            {issue.attachments && issue.attachments.length > 0 && (
              <span className="ml-2">
                {issue.attachments.map((attachment, idx) => (
                  <a
                    key={attachment.id || attachment.name || idx}
                    href={attachment.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-500 hover:text-blue-700 hover:underline ml-1"
                  >
                    {attachment.name}
                  </a>
                ))}
              </span>
            )}
          </div>

          {issue.rootCause && (
            <div>
              <span className="text-xs font-bold text-gray-500">原因分析：</span>
              <span className="text-xs text-gray-700 ml-1">{issue.rootCause}</span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <div>
                <span className="text-xs font-bold text-gray-500">短期措施：</span>
                {issue.actionItems?.filter(a => a.actionType === 'short_term').length > 0 ? (
                  <div className="mt-1 space-y-1">
                    {issue.actionItems.filter(a => a.actionType === 'short_term').map((action, idx) => {
                      const actionStatus = action.status || 'pending';
                      const isExpandable = actionStatus === 'delayed-complete' || actionStatus === 'bp-ng';
                      const isExpanded = expandedDelays.has(`st-${action.id}`);
                      return (
                        <div key={action.id || idx}>
                          <div className="flex items-center gap-2">
                            <div
                              className={`w-4 h-4 border-2 rounded-full flex items-center justify-center transition-all ${ACTION_ITEM_STATUS_COLORS[actionStatus]}`}
                              title={ACTION_ITEM_STATUS_LABELS[actionStatus]}
                            >
                              {actionStatus === 'completed' && (
                                <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                              {actionStatus === 'bp-ng' && (
                                <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="white">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              )}
                              {actionStatus === 'delayed-complete' && (
                                <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                            <span className="text-xs text-gray-700 flex-1">
                              {action.content}
                            </span>
                            {action.breakpointDate && (
                              <span className="text-xs text-gray-400">
                                {action.breakpointDate}{action.breakpointNode && ` ${action.breakpointNode}`}
                              </span>
                            )}
                            {isExpandable && (
                              <button
                                onClick={() => {
                                  const newExpanded = new Set(expandedDelays);
                                  if (isExpanded) {
                                    newExpanded.delete(`st-${action.id}`);
                                  } else {
                                    newExpanded.add(`st-${action.id}`);
                                  }
                                  setExpandedDelays(newExpanded);
                                }}
                                className="p-0.5 hover:bg-gray-100 rounded transition-colors"
                                title="查看详情"
                              >
                                <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                              </button>
                            )}
                            {!isExpandable && (
                              <div className="w-4" />
                            )}
                          </div>
                          {isExpanded && (
                            <div className="ml-6 mt-1 p-2 bg-gray-50 rounded text-xs space-y-1">
                              {action.delayHistory && action.delayHistory.length > 0 ? (
                                action.delayHistory.map((record, rIdx) => (
                                  <div key={record.id || rIdx} className="text-gray-600">
                                    <span className={record.isBpNg ? 'text-red-500' : 'text-orange-500'}>
                                      {record.isBpNg ? 'BP失效' : '延期'}{rIdx + 1}:
                                    </span> {record.delayReason}
                                    <span className="text-gray-400 ml-2">新计划: {record.newPlannedDate}{record.bpNode && ` ${record.bpNode}`}</span>
                                  </div>
                                ))
                              ) : action.delayReason ? (
                                <div className="text-gray-600">
                                  <span className={actionStatus === 'bp-ng' ? 'text-red-500' : 'text-orange-500'}>
                                    {actionStatus === 'bp-ng' ? 'BP失效' : '延期'}:
                                  </span> {action.delayReason}
                                  {action.newPlannedDate && <span className="text-gray-400 ml-2">新计划: {action.newPlannedDate}{action.newBpNode && ` ${action.newBpNode}`}</span>}
                                </div>
                              ) : null}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : issue.shortTermAction ? (
                  <div className="mt-1 flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-blue-500 flex items-center justify-center">
                      <span className="text-white text-[8px]">1</span>
                    </div>
                    <span className="text-xs text-gray-700 flex-1">
                      {issue.shortTermAction}
                      {issue.stBreakpoint && (
                        <span className="text-gray-400 ml-1">
                          {issue.stBreakpoint}{issue.stBreakpointNode && ` ${issue.stBreakpointNode}`}
                        </span>
                      )}
                    </span>
                  </div>
                ) : (
                  <span className="text-xs text-gray-400 ml-1">暂无</span>
                )}
              </div>

              <div>
                <span className="text-xs font-bold text-gray-500">长期措施：</span>
                {issue.actionItems?.filter(a => a.actionType === 'long_term').length > 0 ? (
                  <div className="mt-1 space-y-1">
                    {issue.actionItems.filter(a => a.actionType === 'long_term').map((action, idx) => {
                      const actionStatus = action.status || 'pending';
                      const isExpandable = actionStatus === 'delayed-complete' || actionStatus === 'bp-ng';
                      const isExpanded = expandedDelays.has(`lt-${action.id}`);
                      return (
                        <div key={action.id || idx}>
                          <div className="flex items-center gap-2">
                            <div
                              className={`w-4 h-4 border-2 rounded-full flex items-center justify-center transition-all ${ACTION_ITEM_STATUS_COLORS[actionStatus]}`}
                              title={ACTION_ITEM_STATUS_LABELS[actionStatus]}
                            >
                              {actionStatus === 'completed' && (
                                <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                              {actionStatus === 'bp-ng' && (
                                <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="white">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              )}
                              {actionStatus === 'delayed-complete' && (
                                <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                            <span className="text-xs text-gray-700 flex-1">
                              {action.content}
                            </span>
                            {action.breakpointDate && (
                              <span className="text-xs text-gray-400">
                                {action.breakpointDate}{action.breakpointNode && ` ${action.breakpointNode}`}
                              </span>
                            )}
                            {isExpandable && (
                              <button
                                onClick={() => {
                                  const newExpanded = new Set(expandedDelays);
                                  if (isExpanded) {
                                    newExpanded.delete(`lt-${action.id}`);
                                  } else {
                                    newExpanded.add(`lt-${action.id}`);
                                  }
                                  setExpandedDelays(newExpanded);
                                }}
                                className="p-0.5 hover:bg-gray-100 rounded transition-colors"
                                title="查看详情"
                              >
                                <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                              </button>
                            )}
                            {!isExpandable && (
                              <div className="w-4" />
                            )}
                          </div>
                          {isExpanded && (
                            <div className="ml-6 mt-1 p-2 bg-gray-50 rounded text-xs space-y-1">
                              {action.delayHistory && action.delayHistory.length > 0 ? (
                                action.delayHistory.map((record, rIdx) => (
                                  <div key={record.id || rIdx} className="text-gray-600">
                                    <span className={record.isBpNg ? 'text-red-500' : 'text-orange-500'}>
                                      {record.isBpNg ? 'BP失效' : '延期'}{rIdx + 1}:
                                    </span> {record.delayReason}
                                    <span className="text-gray-400 ml-2">新计划: {record.newPlannedDate}{record.bpNode && ` ${record.bpNode}`}</span>
                                  </div>
                                ))
                              ) : action.delayReason ? (
                                <div className="text-gray-600">
                                  <span className={actionStatus === 'bp-ng' ? 'text-red-500' : 'text-orange-500'}>
                                    {actionStatus === 'bp-ng' ? 'BP失效' : '延期'}:
                                  </span> {action.delayReason}
                                  {action.newPlannedDate && <span className="text-gray-400 ml-2">新计划: {action.newPlannedDate}{action.newBpNode && ` ${action.newBpNode}`}</span>}
                                </div>
                              ) : null}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : issue.longTermAction ? (
                  <div className="mt-1 flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500 flex items-center justify-center">
                      <span className="text-white text-[8px]">1</span>
                    </div>
                    <span className="text-xs text-gray-700 flex-1">
                      {issue.longTermAction}
                      {issue.ltBreakpoint && (
                        <span className="text-gray-400 ml-1">
                          {issue.ltBreakpoint}{issue.ltBreakpointNode && ` ${issue.ltBreakpointNode}`}
                        </span>
                      )}
                    </span>
                  </div>
                ) : (
                  <span className="text-xs text-gray-400 ml-1">暂无</span>
                )}
              </div>
            </div>

            <div>
              <span className="text-xs font-bold text-gray-500">
                工作计划 ({issue.planTasks.filter(t => t.status === 'completed' || t.status === 'delayed-completed').length}/{issue.planTasks.length})：
              </span>
              {issue.planTasks.length > 0 ? (
                <div className="mt-1 space-y-1">
                  {issue.planTasks.map((task) => {
                    const isDelayExpanded = expandedDelays.has(task.id);
                    return (
                      <div key={task.id}>
                        <div className="flex items-center gap-2">
                          <div
                            className={`w-4 h-4 border-2 rounded-full flex items-center justify-center transition-all
                              ${task.status === 'completed' ? 'bg-green-500 border-green-500' : ''}
                              ${task.status === 'delayed' ? 'bg-red-500 border-red-500' : ''}
                              ${task.status === 'delayed-completed' ? 'bg-orange-500 border-orange-500' : ''}
                              ${task.status === 'pending' ? 'border-gray-300' : ''}`}
                            title={PLAN_TASK_STATUS_LABELS[task.status]}
                          >
                            {(task.status === 'completed') && (
                              <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                            {task.status === 'delayed' && (
                              <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="white">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            )}
                            {task.status === 'delayed-completed' && (
                              <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="white">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                          <span className="text-xs text-gray-700 flex-1">
                            {task.content}
                          </span>
                          <span className="text-xs text-gray-400">
                            {task.plannedDate}
                          </span>
                          {(task.status === 'delayed' || task.status === 'delayed-completed') && (
                            <button
                              onClick={() => toggleDelayExpand(task.id)}
                              className="p-0.5 hover:bg-gray-100 rounded transition-colors"
                              title="查看延期记录"
                            >
                              <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${isDelayExpanded ? 'rotate-180' : ''}`} />
                            </button>
                          )}
                          {!(task.status === 'delayed' || task.status === 'delayed-completed') && (
                            <div className="w-4" />
                          )}
                        </div>
                        {isDelayExpanded && (
                          <div className="ml-6 mt-1 p-2 bg-gray-50 rounded text-xs space-y-1">
                            {task.delayHistory && task.delayHistory.length > 0 ? (
                              task.delayHistory.map((record: TaskDelayRecord, idx: number) => (
                                <div key={record.id || idx} className="text-gray-600">
                                  <span className="text-orange-500">延期{idx + 1}:</span> {record.delayReason}
                                  <span className="text-gray-400 ml-2">新计划: {record.newPlannedDate}</span>
                                </div>
                              ))
                            ) : (
                              <div className="text-gray-600">
                                <span className="text-orange-500">当前延期:</span> {task.delayReason || '无'}
                                {task.newPlannedDate && <span className="text-gray-400 ml-2">新计划: {task.newPlannedDate}</span>}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <span className="text-xs text-gray-400 ml-1">暂无</span>
              )}
            </div>
          </div>
        </div>
      )}

      <>

        {confirmConfig?.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-4 max-w-sm w-full mx-4 shadow-xl">
              <p className="text-gray-800 mb-4">{confirmConfig.message}</p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
                >
                  取消
                </button>
                <button
                  onClick={handleConfirm}
                  className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                >
                  确定
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    </div>
  );
}
